import { useCallback, useEffect, useState, type ComponentType, type ReactNode } from "react";
import { AlertTriangle, FileMinus, FileText, Inbox } from "lucide-react";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import { toastApiError } from "@/lib/api-error";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import type { components } from "@dolfin/api-public";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  rowLink,
} from "@/components/PageUi";
import { EInvoiceReferenceSearch } from "@/components/EInvoiceReferenceSearch";

type ReceivedEInvoice = components["schemas"]["OrganisationEInvoiceResponse"];

const PAGE_SIZE = 25;

/** Exactly one of `billId` and `supplierCreditNoteId` is set once ingested; null if it became nothing. */
function ingestedAs(e: ReceivedEInvoice) {
  if (e.supplierCreditNoteId)
    return {
      badge: "Credit note created",
      href: `/supplier-credit-notes/${e.supplierCreditNoteId}`,
      linkLabel: "Credit note",
      Icon: FileMinus,
    };
  if (e.billId)
    return { badge: "Bill created", href: `/bills/${e.billId}`, linkLabel: "Bill", Icon: FileText };
  return null;
}

export interface ReceivedEInvoicesProps {
  /**
   * The router-agnostic component can't import a router's `Link` (useHref throws outside a <Router>), so the host
   * passes one; without it a plain anchor is used.
   */
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
  /** Called instead of following the link, for hosts that navigate by callback. */
  onDocumentClick?: (href: string) => void;
}

/** A document that couldn't become a bill leaves none behind, so this list is the only answer to "we e-invoiced you". */
export function ReceivedEInvoices({ LinkComponent, onDocumentClick }: ReceivedEInvoicesProps = {}) {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();

  const [invoices, setInvoices] = useState<ReceivedEInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reference, setReference] = useState<string | undefined>(undefined);

  // Keyset pages, as on the sent list: a stack of visited cursors, reset by a search.
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const cursor = cursorStack[cursorStack.length - 1];

  const load = useCallback(async () => {
    if (!organisationId) return;
    setLoading(true);
    const { data, error } = await api.GET("/v1/received-e-invoices", {
      params: { query: { cursor, limit: PAGE_SIZE, networkReference: reference } },
    });
    if (error) {
      toastApiError(error, "Failed to load received e-invoices.");
      setFailed(true);
      setLoading(false);
      return;
    }
    setFailed(false);
    setInvoices(data?.data ?? []);
    setNextCursor(data?.meta?.cursors?.next ?? null);
    setLoading(false);
  }, [api, organisationId, reference, cursor]);

  useEffect(() => {
    void load();
  }, [load]);

  const count = invoices.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <EInvoiceReferenceSearch
          value={reference}
          onChange={(v) => {
            setReference(v);
            setCursorStack([]);
          }}
        />
      </div>

      {loading && count === 0 ? (
        <LoadingState label="Loading received documents..." />
      ) : failed ? (
        <ErrorState message="Received documents could not be loaded." onRetry={() => void load()} />
      ) : count === 0 ? (
        <EmptyState icon={Inbox} title={reference ? "No match" : "Nothing received yet"}>
          {reference
            ? `Nothing received with network id ${reference}.`
            : "Once this organisation is registered, invoices and credit notes sent to its participant id arrive here automatically."}
        </EmptyState>
      ) : (
        <TableShell className={loading ? "opacity-60 transition-opacity" : undefined}>
          <Table>
            <TableHeader>
              <HeaderRow>
                <TableHead className={TH}>Supplier</TableHead>
                <TableHead className={TH}>Network</TableHead>
                <TableHead className={TH}>Received</TableHead>
                <TableHead className={TH}>Outcome</TableHead>
                <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                <TableHead className={TH}>
                  <span className="sr-only">Open</span>
                </TableHead>
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {invoices.map((e) => {
                const outcome = ingestedAs(e);
                const open = outcome && onDocumentClick ? () => onDocumentClick(outcome.href) : undefined;
                const link = rowLink(open);
                return (
                  <TableRow key={e.id} {...link} className={cn("align-top", link.className)}>
                    <TableCell className="max-w-[22rem]">
                      <EntityCell
                        name={e.supplierName}
                        fallback="Unknown supplier"
                        secondary={
                          <>
                            {e.invoiceNumber ?? "No invoice number"}
                            {e.networkReference ? (
                              <span className="font-mono">
                                {" · "}
                                {e.network === "Sdi" ? "SDI " : ""}
                                {e.networkReference}
                              </span>
                            ) : null}
                          </>
                        }
                      />
                      {/* Faults are the point of showing a rejected document at all, and they are
                          written for the person reading them rather than as codes. */}
                      {e.faults && e.faults.length > 0 ? (
                        <ul className="mt-2 space-y-1 pl-11">
                          {e.faults.map((f, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-xs whitespace-normal text-muted-foreground">
                              <AlertTriangle
                                className={cn(
                                  "mt-0.5 size-3.5 shrink-0",
                                  f.severity === "Stop" ? "text-red-600" : "text-amber-600",
                                )}
                              />
                              <span>{f.message}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {e.network === "Sdi" ? "SDI" : (e.network ?? "-")}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap text-muted-foreground tabular-nums">
                      {e.receivedAt ? formatDateTime(e.receivedAt) : "-"}
                    </TableCell>
                    <TableCell>
                      {outcome ? (
                        <Badge
                          variant="outline"
                          className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                        >
                          {outcome.badge}
                        </Badge>
                      ) : (
                        /* Nothing was created (rejected, or rarely still mid-flight); the faults explain it. */
                        <Badge
                          variant="outline"
                          className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                        >
                          Not processed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium whitespace-nowrap tabular-nums">
                      {e.totalAmount !== null && e.totalAmount !== undefined
                        ? formatCurrency(e.totalAmount, e.currency)
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}>
                      {outcome ? (
                        <DocumentLink
                          href={outcome.href}
                          LinkComponent={LinkComponent}
                          onDocumentClick={onDocumentClick}
                        >
                          <outcome.Icon className="size-3.5" />
                          {outcome.linkLabel}
                        </DocumentLink>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableShell>
      )}

      <Pager
        page={cursorStack.length + 1}
        hasPrevious={cursorStack.length > 0 && !loading}
        hasNext={!!nextCursor && !loading}
        onPrevious={() => setCursorStack((stack) => stack.slice(0, -1))}
        onNext={() => nextCursor && setCursorStack((stack) => [...stack, nextCursor])}
      />
    </div>
  );
}

function DocumentLink({
  href,
  LinkComponent,
  onDocumentClick,
  children,
}: ReceivedEInvoicesProps & { href: string; children: ReactNode }) {
  const className = "inline-flex items-center gap-1 text-sm font-medium whitespace-nowrap text-primary hover:underline";

  if (onDocumentClick) {
    return (
      <button type="button" onClick={() => onDocumentClick(href)} className={`${className} cursor-pointer`}>
        {children}
      </button>
    );
  }
  if (LinkComponent) {
    return (
      <LinkComponent href={href} className={className}>
        {children}
      </LinkComponent>
    );
  }
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

/** Under Payables, not beside the registration: what arrives is inbound supplier paperwork, like bills. */
export function ReceivedEInvoicesPage({ LinkComponent, onDocumentClick }: ReceivedEInvoicesProps = {}) {
  return (
    <PageContainer>
      <PageHeader
        eyebrow="Payables"
        title="E-invoicing"
        subtitle="Invoices and credit notes your suppliers have sent over an e-invoicing network (Peppol or Italy's SDI). Most become bills or supplier credit notes on their own; anything that could not is listed with the reason."
      />
      <ReceivedEInvoices LinkComponent={LinkComponent} onDocumentClick={onDocumentClick} />
    </PageContainer>
  );
}
